import { useTranslation } from '@crehana/i18n';
import { createImgixPlaceholder, CustomNextImage } from '@crehana/next';
import { ArrowRightOutline } from '@crehana/icons';
import { Container, PrimaryButton, Row, TextLink } from '@crehana/ui';

import {
  createUrlWithSourceQueryParams,
  useCurrentExperiment,
} from '@crehana/analytics';

import ExperimentLayout from '@/shared/components/AmplitudeExperiment/ExperimentLayout';

import {
  StyledCol,
  StyledImage,
  StyledSection,
  TextContainer,
} from './Hero.styles';

const experimentOptions = [
  { value: 'control' },
  { value: 'variant-1' },
  { value: 'variant-2' },
];
const experimentId = 'homepage-hero-cta';

const CtaExperiment: React.FC = () => {
  const { t } = useTranslation();

  const { currentExperiment, loading } = useCurrentExperiment(
    experimentId,
    experimentOptions,
  );

  const isVariant1 = currentExperiment?.value === 'variant-1';
  const isVariant2 = currentExperiment?.value === 'variant-2';

  const registerUrl = createUrlWithSourceQueryParams({
    url: '/onboarding/',
    source: 'Homepage',
    sourceDetail: 'Hero',
  });

  const b2bUrl = createUrlWithSourceQueryParams({
    url: '/onboarding/b2b/name/',
    source: 'Homepage',
    sourceDetail: 'Hero',
  });

  const getCtaLabel = (): string => {
    if (isVariant1 || isVariant2) return t('HERO_CTA_VARIANT');

    return t('HERO_CTA');
  };

  if (loading) {
    return <div className="tw-h-[72px] tw-bg-transparent tw-w-full"></div>;
  }

  return (
    <>
      <PrimaryButton
        htmlProps={{ 'aria-label': t('HERO_CTA') }}
        className="rounded-10 bg-white text-primary-light py-12 mb-24 sm:mr-24 hover:bg-white"
        as="a"
        href={registerUrl}
      >
        {getCtaLabel()}
      </PrimaryButton>
      {!isVariant2 && (
        <TextLink
          href={b2bUrl}
          className="text-white mx-auto md:mx-0 hover:text-white"
          rightIcon={{ component: ArrowRightOutline, size: 14 }}
        >
          {t('HERO_LINK')}
        </TextLink>
      )}
    </>
  );
};

const Hero: React.FC = () => {
  const { t } = useTranslation();

  const heroImage =
    'https://crehana-vercel-images-prd.imgix.net/public/4a6a4a55-1097-41a7-a8cd-9241f2831778-01-1.png?auto=format&fit=clip';

  return (
    <Container full className="overflow-hidden -mt-56 sm:-mt-72">
      <Row className="relative">
        <StyledCol style={{ maxWidth: 1680 }} className="mx-auto">
          <StyledSection className="relative min-h-screen md:min-h-0 lg:h-screen bg-primary-main pt-72 lg:pt-64 flex flex-col  align-center md:flex-row sm:pt-112">
            <TextContainer className="relative z-4 flex flex-col text-center items-center m-auto px-20 sm:mb-56 md:mb-auto md:px-0 md:text-left md:justify-start md:ml-96 lg:ml-144">
              <h1 className="font-h1 text-white text-40 leading-48 md:text-56 md:leading-68">
                {t('HERO_TITLE')}
              </h1>
              <h2 className="font-body3 text-white mt-24 mb-40 lg:text-18 lg:leading-28">
                {t('HERO_SUBTITLE')}
              </h2>
              <div className="flex flex-col sm:flex-row items-baseline md:w-full">
                <ExperimentLayout>
                  <CtaExperiment />
                </ExperimentLayout>
              </div>
            </TextContainer>
            <StyledImage
              className={`relative lg:absolute lg:bottom-0 z-3 sm:mx-auto md:mt-auto md:mr-0 md:ml-auto `}
            >
              {heroImage && (
                <CustomNextImage
                  priority
                  src={heroImage}
                  blurDataURL={createImgixPlaceholder(heroImage)}
                  layout="fill"
                  alt="Hero background"
                  objectFit="contain"
                  objectPosition="left"
                  placeholder="blur"
                />
              )}
            </StyledImage>
          </StyledSection>
        </StyledCol>
      </Row>
    </Container>
  );
};

export default Hero;
